import * as React from 'react';
import { z } from 'zod';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Download, FileText, Globe, Paperclip } from 'lucide-react';
import { SegmentedControl, WorkspaceButton, useToast } from '@/components/ui/workspace';
import { WorkspaceField, WorkspaceInput, WorkspaceSelect } from '@/components/ui/workspace/field';
import { apiClient } from '@/lib/api/client';
import {
  downloadReceivedFile,
  errorMessage,
  useKbSources,
  useReceivedAction,
  useSourceAction,
  vendorKnowledgeKeys,
  type KbVendor,
} from '@/lib/api/vendor-knowledge';
import { Actions, Chip, FormError, InlineForm, Linkish, Quote } from '../kb-bits';
import { formatDate, hostPath, outcomeText, plural } from '../kb-logic';
import { ConfidentialCheck } from '../sources/source-forms';
import { ReasonForm } from './flags';
import { ReviewGroupShell } from './proposals';

/**
 * The sources part of To review (T-0207; E13-F05, E13-F07): files a
 * vendor sent through an upload link, waiting for a curator; captures
 * whose new passages were not read; and sources a check could not use.
 * A received file can be downloaded or accepted only once its malware
 * scan is clean (rule 6).
 */

const BASE = '/api/v1/vendor-knowledge';

const ReceivedRow = z.object({
  id: z.string(),
  vendor_id: z.string(),
  product_id: z.string().nullable(),
  file_name: z.string(),
  content_type: z.string(),
  byte_size: z.number(),
  scan_status: z.string(),
  received_at: z.string(),
  via: z.string(),
  sender_name: z.string().nullable(),
  sender_note: z.string().nullable(),
});
type ReceivedRowT = z.infer<typeof ReceivedRow>;

const WaitingRow = z.object({
  version_id: z.string(),
  version_no: z.number(),
  captured_at: z.string(),
  vendor_id: z.string(),
  source: z.object({
    id: z.string(),
    kind: z.string(),
    url: z.string().nullable(),
    title: z.string().nullable(),
  }),
  changed_segments: z.number(),
  last_run: z.record(z.unknown()).nullable(),
});
type WaitingRowT = z.infer<typeof WaitingRow>;

const ProblemRow = z.object({
  id: z.string(),
  kind: z.string(),
  vendor_id: z.string(),
  product_id: z.string().nullable(),
  url: z.string().nullable(),
  title: z.string().nullable(),
  status: z.string(),
  last_outcome: z.string().nullable(),
  last_checked_at: z.string().nullable(),
});
type ProblemRowT = z.infer<typeof ProblemRow>;

function useRows<T>(
  key: 'received' | 'waiting_to_read' | 'source_problems',
  row: z.ZodType<T>,
  vendorId: string,
) {
  const schema = z.object({
    key: z.string(),
    rows: z.array(row),
    next_cursor: z.string().nullable(),
  });
  return useInfiniteQuery({
    queryKey: vendorKnowledgeKeys.reviewRows(key, vendorId),
    queryFn: ({ pageParam }) =>
      apiClient(`${BASE}/review/${key}`, schema, {
        query: { vendor_id: vendorId, cursor: pageParam ?? undefined },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
}

export function SourcesReviewGroups({
  data,
  received,
  waiting,
  problems,
}: {
  data: KbVendor;
  received: number;
  waiting: number;
  problems: number;
}) {
  return (
    <>
      {received > 0 ? <ReceivedGroup data={data} count={received} /> : null}
      {waiting > 0 ? <WaitingGroup vendorId={data.vendor.id} count={waiting} /> : null}
      {problems > 0 ? <ProblemsGroup vendorId={data.vendor.id} count={problems} /> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Received files
// ---------------------------------------------------------------------------

function ReceivedGroup({ data, count }: { data: KbVendor; count: number }) {
  const rows = useRows('received', ReceivedRow, data.vendor.id);
  const all = rows.data?.pages.flatMap((p) => p.rows) ?? [];
  return (
    <ReviewGroupShell
      id="kb-review-received"
      title="Files received"
      count={count}
      hint="Sent through an upload link. Nothing is read until you accept it, and only once the malware scan finds it clean."
    >
      {all.map((r) => (
        <ReceivedCard key={r.id} row={r} data={data} />
      ))}
    </ReviewGroupShell>
  );
}

function size(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function ReceivedCard({ row: r, data }: { row: ReceivedRowT; data: KbVendor }) {
  const { toast } = useToast();
  const action = useReceivedAction();
  const sources = useKbSources(data.vendor.id);
  const [mode, setMode] = React.useState<'accept' | 'reject' | null>(null);
  const [as, setAs] = React.useState<'new' | 'version'>('new');
  const [title, setTitle] = React.useState(r.file_name.replace(/\.[a-z0-9]+$/i, ''));
  const [providedBy, setProvidedBy] = React.useState(
    r.sender_name ? `${r.sender_name} (${data.vendor.name})` : data.vendor.name,
  );
  const [confidential, setConfidential] = React.useState(false);
  const [versionOf, setVersionOf] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const clean = r.scan_status === 'clean';
  const documents = (sources.data?.sources ?? []).filter(
    (s) => s.kind === 'vendor_document' && s.status !== 'retired',
  );

  const accept = () => {
    setError(null);
    action.mutate(
      {
        id: r.id,
        verb: 'accept',
        body:
          as === 'version'
            ? { as: 'version', source_id: versionOf }
            : {
                as: 'new',
                title: title.trim(),
                provided_by: providedBy.trim(),
                confidential,
                ...(r.product_id ? { product_id: r.product_id } : {}),
              },
      },
      {
        onSuccess: () => {
          toast({
            message: `Accepted ${r.file_name}${confidential && as === 'new' ? ' as vendor-confidential' : ''}. AI reads it now; its passages appear under To review.`,
          });
          setMode(null);
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  return (
    <div
      className={`rounded-card border border-l-4 border-ink-100 bg-paper-0 px-4 py-3.5 ${clean ? 'border-l-accent-amber-600' : 'border-l-posture-critical'}`}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <Paperclip className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="font-medium text-ink-900">{r.file_name}</span>{' '}
          <span className="text-meta text-ink-500">
            {size(r.byte_size)} · received {formatDate(r.received_at)}
            {r.sender_name ? ` from ${r.sender_name}` : ''}
          </span>
        </span>
        {r.scan_status === 'clean' ? (
          <Chip tone="healthy">Scan clean</Chip>
        ) : r.scan_status === 'infected' ? (
          <Chip tone="critical">The scan found malware</Chip>
        ) : (
          <Chip tone="at-risk">Scan could not finish</Chip>
        )}
      </div>
      {r.sender_note ? <Quote text={r.sender_note} className="text-[14px]" /> : null}
      {!clean ? (
        <div className="mt-1.5 text-meta text-ink-500">
          It cannot be downloaded or accepted. Reject it, and ask the vendor for a clean copy if you
          still need it.
        </div>
      ) : null}
      {error ? <FormError>{error}</FormError> : null}
      {mode === 'accept' ? (
        <InlineForm aria-label="Accept the file">
          <SegmentedControl<'new' | 'version'>
            aria-label="Accept it as"
            value={as}
            onChange={setAs}
            options={[
              { value: 'new', label: 'A new document' },
              ...(documents.length
                ? [{ value: 'version' as const, label: 'A new version of one' }]
                : []),
            ]}
          />
          {as === 'version' ? (
            <WorkspaceField label="Which document" className="mt-3">
              {(id) => (
                <WorkspaceSelect
                  id={id}
                  value={versionOf}
                  onChange={(e) => setVersionOf(e.target.value)}
                >
                  <option value="" disabled>
                    Pick a document
                  </option>
                  {documents.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.title ?? 'Untitled'}
                    </option>
                  ))}
                </WorkspaceSelect>
              )}
            </WorkspaceField>
          ) : (
            <>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <WorkspaceField label="Title">
                  {(id) => (
                    <WorkspaceInput
                      id={id}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      autoComplete="off"
                    />
                  )}
                </WorkspaceField>
                <WorkspaceField label="Provided by">
                  {(id) => (
                    <WorkspaceInput
                      id={id}
                      value={providedBy}
                      onChange={(e) => setProvidedBy(e.target.value)}
                      autoComplete="off"
                    />
                  )}
                </WorkspaceField>
              </div>
              <ConfidentialCheck checked={confidential} onChange={setConfidential} />
            </>
          )}
          <Actions>
            <WorkspaceButton
              variant="primary"
              disabled={action.isPending || (as === 'version' ? !versionOf : !title.trim())}
              onClick={accept}
            >
              Accept and read
            </WorkspaceButton>
            <WorkspaceButton variant="tertiary" onClick={() => setMode(null)}>
              Cancel
            </WorkspaceButton>
          </Actions>
        </InlineForm>
      ) : mode === 'reject' ? (
        <ReasonForm
          label="Why reject it?"
          placeholder="For example: not the document we asked for"
          help="A rejected file is never read. It stays on record."
          button="Reject"
          destructive
          pending={action.isPending}
          onCancel={() => setMode(null)}
          onSubmit={(reason) =>
            action.mutate(
              { id: r.id, verb: 'reject', body: { reason } },
              {
                onSuccess: () => toast({ message: `Rejected ${r.file_name}. It won’t be read.` }),
                onError: (err) => setError(errorMessage(err)),
              },
            )
          }
        />
      ) : (
        <Actions>
          {clean ? (
            <>
              <WorkspaceButton variant="primary" onClick={() => setMode('accept')}>
                Accept for reading
              </WorkspaceButton>
              <WorkspaceButton
                onClick={() =>
                  void downloadReceivedFile(r.id, r.file_name).catch((err: unknown) =>
                    setError(errorMessage(err)),
                  )
                }
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" /> Download
              </WorkspaceButton>
            </>
          ) : null}
          <WorkspaceButton variant="tertiary" onClick={() => setMode('reject')}>
            Reject
          </WorkspaceButton>
        </Actions>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Waiting to be read
// ---------------------------------------------------------------------------

function lastRunText(run: Record<string, unknown> | null): string {
  if (!run) return 'Not read yet.';
  const outcome = typeof run['outcome'] === 'string' ? run['outcome'] : null;
  const code = typeof run['error_code'] === 'string' ? run['error_code'] : null;
  if (outcome === 'over_cap')
    return 'Over the limit for an automatic reading. Read it now to go ahead.';
  if (code === 'ai.budget_exhausted')
    return 'The firm AI budget was used up; it is read when there is room.';
  if (run['status'] === 'failed') return 'The last reading did not finish.';
  return `Last reading: ${outcomeText(outcome)}.`;
}

function WaitingGroup({ vendorId, count }: { vendorId: string; count: number }) {
  const rows = useRows('waiting_to_read', WaitingRow, vendorId);
  const all = rows.data?.pages.flatMap((p) => p.rows) ?? [];
  return (
    <ReviewGroupShell
      id="kb-review-waiting"
      title="Waiting to be read"
      count={count}
      hint="Captures with new or changed passages that AI has not read. Reading uses the firm AI budget."
    >
      <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
        {all.map((r) => (
          <WaitingRowView key={r.version_id} row={r} />
        ))}
      </div>
    </ReviewGroupShell>
  );
}

function WaitingRowView({ row: r }: { row: WaitingRowT }) {
  const { toast } = useToast();
  const action = useSourceAction();
  const web = r.source.kind === 'web_page';
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-ink-100 px-3.5 py-2.5 first:border-t-0 sm:flex-nowrap">
      {web ? (
        <Globe className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
      ) : (
        <FileText className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-ink-900">
          {r.source.title ?? (r.source.url ? hostPath(r.source.url) : 'Untitled')}
        </div>
        <div className="text-meta text-ink-500">
          Version {r.version_no}, {formatDate(r.captured_at)} ·{' '}
          {plural(r.changed_segments, 'new or changed passage')}. {lastRunText(r.last_run)}
        </div>
      </div>
      <WorkspaceButton
        disabled={action.isPending}
        onClick={() =>
          action.mutate(
            { id: r.source.id, verb: 'read-now', body: { version_id: r.version_id } },
            {
              onSuccess: (out) => {
                const cents =
                  typeof out['estimate_cents'] === 'number' ? out['estimate_cents'] : null;
                toast({
                  message: `Reading started${cents !== null ? `, estimated at $${(cents / 100).toFixed(2)}` : ''}. Its proposals appear under To review.`,
                });
              },
              onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
            },
          )
        }
      >
        Read now
      </WorkspaceButton>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sources with problems
// ---------------------------------------------------------------------------

function problemText(r: ProblemRowT): string {
  if (r.status === 'pending') return 'Its web domain was rejected, so it is never captured.';
  return `The last check: ${outcomeText(r.last_outcome)}${r.last_checked_at ? `, ${formatDate(r.last_checked_at)}` : ''}.`;
}

function ProblemsGroup({ vendorId, count }: { vendorId: string; count: number }) {
  const rows = useRows('source_problems', ProblemRow, vendorId);
  const all = rows.data?.pages.flatMap((p) => p.rows) ?? [];
  return (
    <ReviewGroupShell
      id="kb-review-problems"
      title="Sources with problems"
      count={count}
      hint="A check could not use these. Check again, add the page’s new address, or retire the source."
    >
      <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
        {all.map((r) => (
          <ProblemRowView key={r.id} row={r} />
        ))}
      </div>
    </ReviewGroupShell>
  );
}

function ProblemRowView({ row: r }: { row: ProblemRowT }) {
  const { toast } = useToast();
  const action = useSourceAction();
  const [retiring, setRetiring] = React.useState(false);
  return (
    <div className="border-t border-ink-100 px-3.5 py-2.5 first:border-t-0">
      <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
        <Globe className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-ink-900">
            {r.title ?? (r.url ? hostPath(r.url) : 'Untitled')}
          </div>
          <div className="text-meta text-ink-500">
            {r.url ? <span className="font-mono text-[11px]">{hostPath(r.url)} · </span> : null}
            {problemText(r)}
          </div>
        </div>
        {r.status === 'active' ? (
          <Linkish
            disabled={action.isPending}
            onClick={() =>
              action.mutate(
                { id: r.id, verb: 'check-now' },
                {
                  onSuccess: () => toast({ message: 'Checking it again now.' }),
                  onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
                },
              )
            }
          >
            Check now
          </Linkish>
        ) : null}
        {!retiring ? (
          <Linkish tone="quiet" onClick={() => setRetiring(true)}>
            Retire
          </Linkish>
        ) : null}
      </div>
      {retiring ? (
        <ReasonForm
          label="Why retire this source?"
          placeholder="For example: the vendor took the page down"
          help="Its items keep their citations. A retired source is not checked or read."
          button="Retire"
          destructive
          pending={action.isPending}
          onCancel={() => setRetiring(false)}
          onSubmit={(reason) =>
            action.mutate(
              { id: r.id, verb: 'retire', body: { reason } },
              {
                onSuccess: () => toast({ message: 'Retired. It is no longer checked.' }),
                onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
              },
            )
          }
        />
      ) : null}
    </div>
  );
}
